/** Where a capture page streams the voice, and how it speaks there. */
export interface SpeechEndpoint {
  /** The socket: wss://, or ws:// for a dev backend. */
  socket: string;
  /** The frames' prefix: `<frames>.start`, `<frames>.partial`… */
  frames: string;
  /** Whether the page hands back each hold's audio, to be heard again. */
  clip: boolean;
  /**
   * How long the page keeps the microphone after a hold. Each composer has
   * a page of its own, and iOS lets one page capture at a time: a page
   * that keeps it starves the next one's hold.
   */
  keepMicrophoneMs: number;
}

/**
 * A socket URL on a backend endpoint: wss for https (and wss), ws for a
 * dev http (or ws) one only. On the web an endpoint may be relative to the
 * page.
 */
export function speechSocketUrl(
  endpoint: string,
  path: string,
  allowWs: boolean,
): string {
  const url = new URL(
    endpoint,
    typeof window !== 'undefined' ? window.location?.href : undefined,
  );
  if (url.protocol === 'https:' || url.protocol === 'wss:')
    url.protocol = 'wss:';
  else if (allowWs && (url.protocol === 'http:' || url.protocol === 'ws:'))
    url.protocol = 'ws:';
  else throw new Error('Invalid speech endpoint');
  if (url.username || url.password || url.search || url.hash)
    throw new Error('Invalid speech endpoint');
  url.pathname = path;
  return url.toString();
}

/**
 * Dictation, on chat-gateway's own socket for it: the words of any text
 * box, nothing of the voice kept (backend `docs/dictation/spec.md`).
 */
export function dictationEndpoint(
  chatEndpoint: string,
  allowWs: boolean,
): SpeechEndpoint {
  return {
    socket: speechSocketUrl(chatEndpoint, '/dictation/stream', allowWs),
    frames: 'dictation',
    clip: false,
    // Words now and then, in any of several boxes: let go at once.
    keepMicrophoneMs: 0,
  };
}
